<template>
    <b-container id="source-management" v-if="$can('view-source-management') || $can('view-all')" fluid>
        <b-row class="page-title">
            <b-container fluid>
                <b-row>
                    <b-col cols="8">
                        <h3>Source Management</h3>
                    </b-col>
                </b-row>
                <hr />
            </b-container>
        </b-row>

        <b-tabs content-class="mt-3">
            <b-tab title="Sources" active>
                <b-row class="mb-3">
                    <b-col cols="12" class="text-right">
                        <b-button variant="primary" size="sm" @click="openSourceModal()">
                            <i class="fas fa-plus"></i> New Source
                        </b-button>
                    </b-col>
                </b-row>

                <b-table
                    :items="sources"
                    :fields="sourceFields"
                    :busy="sourcesBusy"
                    hover
                    responsive
                    striped
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(description)="row">
                        {{ row.item.description ? row.item.description : '—' }}
                    </template>

                    <template v-slot:cell(source_type)="row">
                        {{ row.item.source_type ? row.item.source_type.name : (row.item.sourceType ? row.item.sourceType.name : '-') }}
                    </template>

                    <template v-slot:cell(active)="row">
                        <b-badge :variant="row.item.active ? 'success' : 'secondary'">
                            {{ row.item.active ? 'Sync On' : 'Sync Off' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(track_inventory)="row">
                        <b-badge :variant="row.item.track_inventory ? 'info' : 'light'">
                            {{ row.item.track_inventory ? 'Yes' : 'No' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="openSourceModal(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-danger" @click="deleteSource(row.item)">
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>
            </b-tab>

            <b-tab title="Source Actions">
                <b-row class="mb-3">
                    <b-col cols="12" class="text-right">
                        <b-button variant="primary" size="sm" @click="openActionModal()">
                            <i class="fas fa-plus"></i> New Action
                        </b-button>
                    </b-col>
                </b-row>

                <b-table
                    :items="actions"
                    :fields="actionFields"
                    :busy="actionsBusy"
                    hover
                    responsive
                    striped
                >
                    <template v-slot:table-busy>
                        <div class="text-center text-danger my-2">
                            <b-spinner class="align-middle"></b-spinner>
                            <strong>Loading...</strong>
                        </div>
                    </template>

                    <template v-slot:cell(target_source)="row">
                        {{ actionSourceLabel(row.item) }}
                    </template>

                    <template v-slot:cell(payload)="row">
                        <small>{{ formatPayload(row.item.payload) }}</small>
                    </template>

                    <template v-slot:cell(active)="row">
                        <b-badge :variant="row.item.active ? 'success' : 'secondary'">
                            {{ row.item.active ? 'Enabled' : 'Disabled' }}
                        </b-badge>
                    </template>

                    <template v-slot:cell(actions)="row">
                        <b-button-group size="sm">
                            <b-button variant="outline-primary" @click="openActionModal(row.item)">
                                <i class="fas fa-edit"></i>
                            </b-button>
                            <b-button variant="outline-danger" @click="deleteAction(row.item)">
                                <i class="fas fa-trash"></i>
                            </b-button>
                        </b-button-group>
                    </template>
                </b-table>
            </b-tab>
        </b-tabs>

        <b-modal
            id="source-modal"
            :title="sourceEdit ? 'Edit Source' : 'Create Source'"
            @hidden="resetSourceForm"
            @ok.prevent="saveSource"
            :ok-disabled="!isSourceValid"
        >
            <b-form @submit.prevent="saveSource">
                <b-form-group
                    label="Label"
                    label-for="source-description"
                    description="Friendly name so you can tell stores apart (e.g. Black Sheep US Wholesale)."
                >
                    <b-form-input id="source-description" v-model="sourceForm.description" maxlength="255"></b-form-input>
                </b-form-group>

                <b-form-group
                    label="Host / Domain"
                    label-for="source-name"
                    :description="hostDescription"
                >
                    <b-form-input id="source-name" v-model="sourceForm.name" required></b-form-input>
                </b-form-group>

                <b-form-group label="Source Type" label-for="source-type">
                    <b-form-select
                        id="source-type"
                        v-model="sourceForm.sources_types_id"
                        :options="sourceTypeOptions"
                        required
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="API Key" label-for="source-api-key">
                    <b-form-input id="source-api-key" v-model="sourceForm.api_key"></b-form-input>
                </b-form-group>

                <b-form-group label="API Password" label-for="source-api-password">
                    <b-form-input id="source-api-password" v-model="sourceForm.api_password" type="password" autocomplete="new-password"></b-form-input>
                </b-form-group>

                <b-form-group label="API Token" label-for="source-api-token" :description="tokenDescription">
                    <b-form-input id="source-api-token" v-model="sourceForm.api_token"></b-form-input>
                </b-form-group>

                <b-form-group label="Webhook Key" label-for="source-webhook-key" :description="webhookKeyDescription">
                    <b-form-input id="source-webhook-key" v-model="sourceForm.webhook_key"></b-form-input>
                </b-form-group>

                <b-form-group label="Username" label-for="source-username">
                    <b-form-input id="source-username" v-model="sourceForm.user_username"></b-form-input>
                </b-form-group>

                <b-form-group label="Password" label-for="source-password">
                    <b-form-input id="source-password" v-model="sourceForm.user_password" type="password" autocomplete="new-password"></b-form-input>
                </b-form-group>

                <b-form-group
                    description="Controls outbound product/quantity sync only. Webhooks and Source Actions still use this source when sync is off."
                >
                    <b-form-checkbox v-model="sourceForm.active" switch>Product Sync</b-form-checkbox>
                </b-form-group>

                <b-form-group description="When on, inventory quantity updates are pushed to this source.">
                    <b-form-checkbox v-model="sourceForm.track_inventory" switch>Track Inventory</b-form-checkbox>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('source-modal')">Cancel</b-button>
                <b-button variant="primary" @click="saveSource" :disabled="!isSourceValid">
                    {{ sourceEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>

        <b-modal
            id="action-modal"
            :title="actionEdit ? 'Edit Source Action' : 'Create Source Action'"
            @hidden="resetActionForm"
            @ok.prevent="saveAction"
            :ok-disabled="!isActionValid"
        >
            <b-form @submit.prevent="saveAction">
                <b-form-group label="Name" label-for="action-name">
                    <b-form-input id="action-name" v-model="actionForm.name" required></b-form-input>
                </b-form-group>

                <b-form-group label="Event" label-for="action-event">
                    <b-form-select
                        id="action-event"
                        v-model="actionForm.event"
                        :options="eventOptions"
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Match Type" label-for="action-match-type">
                    <b-form-select
                        id="action-match-type"
                        v-model="actionForm.match_type"
                        :options="matchTypeOptions"
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Match Value" label-for="action-match-value" description="e.g. Black Sheep">
                    <b-form-input id="action-match-value" v-model="actionForm.match_value" required></b-form-input>
                </b-form-group>

                <b-form-group label="Action" label-for="action-action">
                    <b-form-select
                        id="action-action"
                        v-model="actionForm.action"
                        :options="actionTypeOptions"
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Target Source" label-for="action-target-source">
                    <b-form-select
                        id="action-target-source"
                        v-model="actionForm.target_sources_id"
                        :options="targetSourceOptions"
                    ></b-form-select>
                </b-form-group>

                <b-form-group label="Split Status (optional)" label-for="action-split-status" description="Applied when a split child is created for this vendor (e.g. Pending)">
                    <b-form-input id="action-split-status" v-model="actionForm.split_status"></b-form-input>
                </b-form-group>

                <b-form-group label="Shopify Tags (comma-separated)" label-for="action-tags">
                    <b-form-input id="action-tags" v-model="actionForm.tags"></b-form-input>
                </b-form-group>

                <b-form-group label="Store customer ID (optional)" label-for="action-customer-id" description="Shopify or Bagisto customer id used when this action creates an order.">
                    <b-form-input id="action-customer-id" v-model="actionForm.shopify_customer_id"></b-form-input>
                </b-form-group>

                <b-form-group description="When disabled, this rule will not run. The target source can still receive webhooks and keep product sync off independently.">
                    <b-form-checkbox v-model="actionForm.active" switch>Action Enabled</b-form-checkbox>
                </b-form-group>
            </b-form>

            <template v-slot:modal-footer>
                <b-button variant="secondary" @click="$bvModal.hide('action-modal')">Cancel</b-button>
                <b-button variant="primary" @click="saveAction" :disabled="!isActionValid">
                    {{ actionEdit ? 'Update' : 'Create' }}
                </b-button>
            </template>
        </b-modal>
    </b-container>
</template>

<script>
export default {
    name: 'SourceManagement',

    data() {
        return {
            sources: [],
            sourceTypes: [],
            actions: [],
            sourcesBusy: false,
            actionsBusy: false,
            sourceEdit: false,
            actionEdit: false,
            sourceFields: [
                { key: 'id', label: 'ID', sortable: true },
                { key: 'description', label: 'Label', sortable: true },
                { key: 'name', label: 'Host / Domain', sortable: true },
                { key: 'source_type', label: 'Type', sortable: false },
                { key: 'active', label: 'Product Sync', sortable: true },
                { key: 'track_inventory', label: 'Track Inv.', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            actionFields: [
                { key: 'name', label: 'Name', sortable: true },
                { key: 'event', label: 'Event', sortable: true },
                { key: 'match_type', label: 'Match Type', sortable: true },
                { key: 'match_value', label: 'Match Value', sortable: true },
                { key: 'action', label: 'Action', sortable: true },
                { key: 'target_source', label: 'Target Source', sortable: false },
                { key: 'payload', label: 'Payload', sortable: false },
                { key: 'active', label: 'Enabled', sortable: true },
                { key: 'actions', label: 'Actions', sortable: false }
            ],
            eventOptions: [
                { value: 'order.split', text: 'order.split' },
                { value: 'order.confirmed', text: 'order.confirmed' }
            ],
            matchTypeOptions: [
                { value: 'vendor_name', text: 'vendor_name' },
                { value: 'order_tag', text: 'order_tag' }
            ],
            actionTypeOptions: [
                { value: 'create_order', text: 'create_order' }
            ],
            sourceForm: {
                id: null,
                name: '',
                sources_types_id: null,
                description: '',
                api_key: '',
                api_password: '',
                api_token: '',
                user_username: '',
                user_password: '',
                webhook_key: '',
                active: true,
                track_inventory: false
            },
            actionForm: {
                id: null,
                name: '',
                event: 'order.confirmed',
                match_type: 'vendor_name',
                match_value: '',
                action: 'create_order',
                target_sources_id: null,
                split_status: 'Pending',
                tags: '',
                shopify_customer_id: '',
                active: true
            }
        }
    },

    computed: {
        sourceTypeOptions() {
            return [
                { value: null, text: 'Select type...' },
                ...this.sourceTypes.map((type) => ({
                    value: type.id,
                    text: type.name
                }))
            ]
        },

        targetSourceOptions() {
            return [
                { value: null, text: 'Select source...' },
                ...this.sources.map((source) => ({
                    value: source.id,
                    text: this.sourceDisplayName(source)
                }))
            ]
        },

        isSourceValid() {
            return this.sourceForm.name.trim() !== '' && !!this.sourceForm.sources_types_id
        },

        selectedSourceTypeName() {
            const type = this.sourceTypes.find((row) => String(row.id) === String(this.sourceForm.sources_types_id))
            return type ? type.name : ''
        },

        hostDescription() {
            if (this.selectedSourceTypeName === 'Bagisto') {
                return 'Bagisto site URL, for example https://staging.example.com'
            }
            return 'Technical connection value used by the API. Shopify shop slug (without .myshopify.com) or ERP hostname.'
        },

        tokenDescription() {
            if (this.selectedSourceTypeName === 'Bagisto') {
                return 'Admin Integration token from Settings → Integration. Format id|token. No storefront key.'
            }
            return ''
        },

        webhookKeyDescription() {
            if (this.selectedSourceTypeName === 'Bagisto') {
                return 'Signing key from Bagisto Settings → Webhooks. The webhook URL is /webhooks/bagisto/{source id}.'
            }
            return ''
        },

        isActionValid() {
            return (
                this.actionForm.name.trim() !== '' &&
                this.actionForm.match_value.trim() !== '' &&
                !!this.actionForm.target_sources_id
            )
        }
    },

    methods: {
        emptySourceForm() {
            return {
                id: null,
                name: '',
                sources_types_id: null,
                description: '',
                api_key: '',
                api_password: '',
                api_token: '',
                user_username: '',
                user_password: '',
                webhook_key: '',
                active: true,
                track_inventory: false
            }
        },

        emptyActionForm() {
            return {
                id: null,
                name: '',
                event: 'order.confirmed',
                match_type: 'vendor_name',
                match_value: '',
                action: 'create_order',
                target_sources_id: null,
                split_status: 'Pending',
                tags: '',
                shopify_customer_id: '',
                active: true
            }
        },

        sourceDisplayName(source) {
            if (!source) {
                return '-'
            }
            const label = (source.description || '').trim()
            if (label) {
                return `${label} — ${source.name} (#${source.id})`
            }
            return `${source.name} (#${source.id})`
        },

        actionSourceLabel(action) {
            const source = action.target_source || action.targetSource
            return this.sourceDisplayName(source)
        },

        formatPayload(payload) {
            if (!payload) {
                return '-'
            }
            try {
                return JSON.stringify(payload)
            } catch (e) {
                return '-'
            }
        },

        async loadSources() {
            this.sourcesBusy = true
            try {
                const response = await axios.get('/api/company/sources')
                if (response.data.status === 'success') {
                    this.sources = response.data.sources
                    this.sourceTypes = response.data.source_types || []
                }
            } catch (error) {
                this.showAlert('error', 'Error', error.response?.data?.message || 'Failed to load sources')
            } finally {
                this.sourcesBusy = false
            }
        },

        async loadActions() {
            this.actionsBusy = true
            try {
                const response = await axios.get('/api/company/source-actions')
                if (response.data.status === 'success') {
                    this.actions = response.data.source_actions
                }
            } catch (error) {
                this.showAlert('error', 'Error', error.response?.data?.message || 'Failed to load source actions')
            } finally {
                this.actionsBusy = false
            }
        },

        openSourceModal(source = null) {
            this.sourceEdit = !!source
            if (source) {
                this.sourceForm = {
                    id: source.id,
                    name: source.name || '',
                    sources_types_id: source.sources_types_id,
                    description: source.description || '',
                    api_key: source.api_key || '',
                    api_password: source.api_password || '',
                    api_token: source.api_token || '',
                    user_username: source.user_username || '',
                    user_password: source.user_password || '',
                    webhook_key: source.webhook_key || '',
                    active: Boolean(source.active),
                    track_inventory: Boolean(source.track_inventory)
                }
            } else {
                this.resetSourceForm()
            }
            this.$bvModal.show('source-modal')
        },

        async saveSource() {
            try {
                const url = this.sourceEdit
                    ? `/api/company/sources/${this.sourceForm.id}`
                    : '/api/company/sources'
                const method = this.sourceEdit ? 'put' : 'post'
                const response = await axios[method](url, this.sourceForm)
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.$bvModal.hide('source-modal')
                    await this.loadSources()
                }
            } catch (error) {
                this.showAlert('error', 'Error', error.response?.data?.message || 'Failed to save source')
            }
        },

        async deleteSource(source) {
            if (!confirm(`Delete source "${this.sourceDisplayName(source)}"? Related actions will also be removed.`)) {
                return
            }
            try {
                const response = await axios.delete(`/api/company/sources/${source.id}`)
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    await Promise.all([this.loadSources(), this.loadActions()])
                }
            } catch (error) {
                this.showAlert('error', 'Error', error.response?.data?.message || 'Failed to delete source')
            }
        },

        resetSourceForm() {
            this.sourceForm = this.emptySourceForm()
            this.sourceEdit = false
        },

        openActionModal(action = null) {
            this.actionEdit = !!action
            if (action) {
                const payload = action.payload || {}
                this.actionForm = {
                    id: action.id,
                    name: action.name || '',
                    event: action.event,
                    match_type: action.match_type,
                    match_value: action.match_value || '',
                    action: action.action,
                    target_sources_id: action.target_sources_id,
                    split_status: payload.split_status || '',
                    tags: Array.isArray(payload.tags) ? payload.tags.join(', ') : '',
                    shopify_customer_id: payload.shopify_customer_id || '',
                    active: Boolean(action.active)
                }
            } else {
                this.resetActionForm()
            }
            this.$bvModal.show('action-modal')
        },

        buildActionPayload() {
            const payload = {}
            if (this.actionForm.split_status && this.actionForm.split_status.trim() !== '') {
                payload.split_status = this.actionForm.split_status.trim()
            }
            if (this.actionForm.tags && this.actionForm.tags.trim() !== '') {
                payload.tags = this.actionForm.tags
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean)
            }
            if (this.actionForm.shopify_customer_id && String(this.actionForm.shopify_customer_id).trim() !== '') {
                payload.shopify_customer_id = String(this.actionForm.shopify_customer_id).trim()
            }
            return Object.keys(payload).length ? payload : null
        },

        async saveAction() {
            try {
                const body = {
                    name: this.actionForm.name,
                    event: this.actionForm.event,
                    match_type: this.actionForm.match_type,
                    match_value: this.actionForm.match_value,
                    action: this.actionForm.action,
                    target_sources_id: this.actionForm.target_sources_id,
                    payload: this.buildActionPayload(),
                    active: this.actionForm.active
                }
                const url = this.actionEdit
                    ? `/api/company/source-actions/${this.actionForm.id}`
                    : '/api/company/source-actions'
                const method = this.actionEdit ? 'put' : 'post'
                const response = await axios[method](url, body)
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    this.$bvModal.hide('action-modal')
                    await this.loadActions()
                }
            } catch (error) {
                this.showAlert('error', 'Error', error.response?.data?.message || 'Failed to save source action')
            }
        },

        async deleteAction(action) {
            if (!confirm(`Delete action "${action.name}"?`)) {
                return
            }
            try {
                const response = await axios.delete(`/api/company/source-actions/${action.id}`)
                if (response.data.status === 'success') {
                    this.showAlert('success', 'Success', response.data.message)
                    await this.loadActions()
                }
            } catch (error) {
                this.showAlert('error', 'Error', error.response?.data?.message || 'Failed to delete source action')
            }
        },

        resetActionForm() {
            this.actionForm = this.emptyActionForm()
            this.actionEdit = false
        },

        showAlert(type, title, message) {
            this.$bvToast.toast(message, {
                title: title,
                variant: type,
                solid: true,
                autoHideDelay: 5000
            })
        }
    },

    async mounted() {
        await this.loadSources()
        await this.loadActions()
    }
}
</script>

<style scoped>
#source-management {
    margin-top: 80px;
}

.page-title {
    margin-bottom: 20px;
}
</style>
